/**
 * §4.4's order list and detail, including the tracking timeline.
 *
 * The list is newest first, which is the opposite of the wholesaler's queue and
 * right on this side: a merchant opens this to check on the thing they ordered
 * this morning, and a wholesaler opens theirs to pack the thing that has been
 * waiting longest.
 *
 * "Receive stock" is the button that matters. §4.3 wants the merchant to check a
 * delivery in while the driver is still there, so it is on the row rather than
 * two screens deep — and what it records is not only what arrived but what did
 * not, because the gap between ordered and received is the thing worth having.
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import {
  Card,
  EmptyState,
  ErrorState,
  Pill,
  SkeletonRows,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  type PillTone,
} from "@/components/ui";
import { formatDate, formatDateTime, formatMoney } from "@/lib/format";
import {
  marketplaceRepository,
  type OrderStatus,
  type PaymentStatus,
} from "@/repositories/marketplaceRepository";

import { RetailerMark } from "./MarketplaceShowcase";

const STATUS_TONES: Record<OrderStatus, PillTone> = {
  SCHEDULED: "info",
  PLACED: "info",
  SUPPLIER_CONFIRMED: "info",
  PREPARING: "warn",
  READY_FOR_COLLECTION: "success",
  COLLECTED: "success",
  DISPATCHED: "warn",
  OUT_FOR_DELIVERY: "warn",
  DELIVERED: "success",
  PARTIALLY_DELIVERED: "warn",
  CANCELLED: "neutral",
  RETURNED: "danger",
  REFUND_IN_PROGRESS: "warn",
  REFUNDED: "neutral",
};

const STATUS_LABELS: Record<OrderStatus, string> = {
  SCHEDULED: "Scheduled",
  PLACED: "Placed",
  SUPPLIER_CONFIRMED: "Confirmed",
  PREPARING: "Being prepared",
  READY_FOR_COLLECTION: "Ready to collect",
  COLLECTED: "Collected",
  DISPATCHED: "Dispatched",
  OUT_FOR_DELIVERY: "Out for delivery",
  DELIVERED: "Delivered",
  PARTIALLY_DELIVERED: "Part delivered",
  CANCELLED: "Cancelled",
  RETURNED: "Returned",
  REFUND_IN_PROGRESS: "Refunding",
  REFUNDED: "Refunded",
};

const PAYMENT_TONES: Record<PaymentStatus, PillTone> = {
  UNPAID: "warn",
  PENDING: "info",
  PAID: "success",
  ON_ACCOUNT: "info",
  FAILED: "danger",
  REFUNDED: "neutral",
};

export function MarketplaceOrders() {
  const [open, setOpen] = useState<string | null>(null);

  const orders = useQuery({
    queryKey: ["marketplace", "orders"],
    queryFn: () => marketplaceRepository.orders(),
  });

  const detail = useQuery({
    queryKey: ["marketplace", "order", open],
    queryFn: () => marketplaceRepository.order(open!),
    enabled: open !== null,
  });

  return (
    <div className="space-y-5">
      <Card flush className="p-5">
        {orders.isError ? (
          <ErrorState
            title="Orders could not load"
            onRetry={() => orders.refetch()}
          />
        ) : orders.isLoading ? (
          <SkeletonRows rows={6} />
        ) : orders.data && orders.data.results.length > 0 ? (
          <Table>
            <THead>
              <TH>Order</TH>
              <TH hideBelow="sm">Supplier</TH>
              <TH hideBelow="lg">Placed</TH>
              <TH hideBelow="md" className="text-right">
                Items
              </TH>
              <TH className="text-right">Total</TH>
              <TH hideBelow="md">Payment</TH>
              <TH>Status</TH>
            </THead>
            <TBody>
              {orders.data.results.map((order) => (
                <TR
                  key={order.id}
                  onClick={() => setOpen(order.id === open ? null : order.id)}
                >
                  <TD className="font-mono text-xs font-medium text-brand-600">
                    {order.number}
                  </TD>
                  <TD hideBelow="sm" className="text-sm text-ink-900">
                    <RetailerMark supplier={order.wholesaler} />
                  </TD>
                  <TD hideBelow="lg" className="text-sm text-ink-500">
                    {order.placed_at ? formatDate(order.placed_at) : "—"}
                  </TD>
                  <TD
                    hideBelow="md"
                    className="text-right tabular-nums text-ink-500"
                  >
                    {order.item_count}
                  </TD>
                  <TD className="text-right tabular-nums">
                    {formatMoney(order.total)}
                  </TD>
                  <TD hideBelow="md">
                    <Pill tone={PAYMENT_TONES[order.payment_status]}>
                      {order.payment_status === "ON_ACCOUNT"
                        ? "On account"
                        : order.payment_status}
                    </Pill>
                  </TD>
                  <TD>
                    <Pill tone={STATUS_TONES[order.status]}>
                      {STATUS_LABELS[order.status]}
                    </Pill>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        ) : (
          <EmptyState
            title="No orders yet"
            description="Anything you buy from the marketplace appears here."
          />
        )}
      </Card>

      {open && detail.data && (
        <Card className="space-y-4">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="font-mono text-sm font-semibold text-ink-900">
              {detail.data.number}
            </h2>
            <Pill tone={STATUS_TONES[detail.data.status]}>
              {STATUS_LABELS[detail.data.status]}
            </Pill>
          </div>

          {detail.data.pool && (
            <p className="rounded-control bg-money-50 p-2 text-sm text-money-700">
              Bought together with {detail.data.pool.progress.merchant_count}{" "}
              other shops.
            </p>
          )}

          <Table>
            <THead>
              <TH>Product</TH>
              <TH className="text-right">Ordered</TH>
              <TH hideBelow="sm" className="text-right">
                Received
              </TH>
              <TH hideBelow="md" className="text-right">
                Price
              </TH>
              <TH className="text-right">Total</TH>
            </THead>
            <TBody>
              {detail.data.lines.map((line) => (
                <TR key={line.id}>
                  <TD className="text-sm text-ink-900">
                    {line.name}
                    {line.pack_size && (
                      <span className="ml-2 text-xs text-ink-500">
                        {line.pack_size}
                      </span>
                    )}
                  </TD>
                  <TD className="text-right tabular-nums text-ink-500">
                    {line.quantity}
                  </TD>
                  <TD
                    hideBelow="sm"
                    className={
                      line.outstanding_quantity > 0
                        ? "text-right font-medium tabular-nums text-warn-700"
                        : "text-right tabular-nums text-ink-500"
                    }
                  >
                    {line.received_quantity}
                  </TD>
                  <TD
                    hideBelow="md"
                    className="text-right tabular-nums text-ink-500"
                  >
                    {formatMoney(line.unit_price)}
                  </TD>
                  <TD className="text-right tabular-nums">
                    {formatMoney(line.total)}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>

          <dl className="ml-auto w-full max-w-xs space-y-1.5 text-sm">
            <Line label="Subtotal" value={detail.data.subtotal} />
            {Number(detail.data.discount_amount) > 0 && (
              <Line label="Saved" value={`-${detail.data.discount_amount}`} />
            )}
            {Number(detail.data.delivery_fee) > 0 && (
              <Line label="Delivery" value={detail.data.delivery_fee} />
            )}
            {Number(detail.data.tax_amount) > 0 && (
              <Line label="VAT" value={detail.data.tax_amount} />
            )}
            <Line label="Total" value={detail.data.total} strong />
          </dl>

          {/* §4.4's tracking timeline. Read newest at the bottom, because that
              is how a conversation reads. */}
          <div className="border-t border-line pt-3">
            <h3 className="mb-2 text-sm font-semibold text-ink-900">
              Progress
            </h3>
            <ol className="space-y-2">
              {detail.data.events.map((event) => (
                <li key={event.id} className="flex gap-3 text-sm">
                  <span className="w-36 shrink-0 text-xs text-ink-500">
                    {formatDateTime(event.created_at)}
                  </span>
                  <span className="text-ink-900">
                    {STATUS_LABELS[event.status]}
                    {event.note && (
                      <span className="text-ink-500"> — {event.note}</span>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </Card>
      )}
    </div>
  );
}

function Line({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div className="flex justify-between gap-4">
      <dt className={strong ? "font-medium text-ink-900" : "text-ink-500"}>
        {label}
      </dt>
      <dd
        className={
          strong
            ? "font-medium tabular-nums text-ink-900"
            : "tabular-nums text-ink-600"
        }
      >
        {formatMoney(value)}
      </dd>
    </div>
  );
}
